import React from 'react';
import { UserCheck, X, AlertTriangle } from 'lucide-react';
import Button from './Button';

interface ImpersonationBannerProps {
  userName: string;
  userEmail: string;
  onExitImpersonation: () => void;
}

/**
 * Banner shown while an admin is simulating another user: who they are viewing
 * as, and the way out.
 *
 * Deliberately NOT positioned here. Layout pins it and measures its height so
 * the rest of the chrome can move down by exactly that much — when this element
 * owned `fixed top-0` itself, it sat on top of the page header and covered the
 * controls in it (issue: simulation bar covers heading functions).
 */
const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({
  userName,
  userEmail,
  onExitImpersonation
}) => {
  return (
    <div className="bg-amber-500 dark:bg-amber-600 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Left side - Icon and message */}
          <div className="flex items-center gap-3 text-amber-950 dark:text-amber-50">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <UserCheck className="h-5 w-5 flex-shrink-0" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
              <span className="font-semibold text-sm sm:text-base">
                Viewing as:
              </span>
              <span className="text-sm sm:text-base">
                <span className="font-medium">{userName}</span>
                <span className="hidden sm:inline"> • </span>
                <span className="text-amber-800 dark:text-amber-200 text-xs sm:text-sm block sm:inline">
                  {userEmail}
                </span>
              </span>
            </div>
          </div>
          
          {/* Right side - Exit button */}
          <Button
            variant="secondary"
            size="sm"
            icon={X}
            onClick={onExitImpersonation}
            className="bg-white/90 hover:bg-white text-amber-900 border-amber-700 hover:border-amber-800 flex-shrink-0"
          >
            <span className="hidden sm:inline">Exit Simulation</span>
            <span className="sm:hidden">Exit</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;

